import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, Download, AlertTriangle } from 'lucide-react';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import locationService from '../../services/api/locationService';
import type {
  BulkLocationValidateResponse,
  BulkLocationValidateRow,
} from '../../services/api/locationService';
import {
  DropZone,
  DropLabel,
  DropSubLabel,
  FileNameChip,
  SummaryBar,
  ValidCount,
  InvalidCount,
  TotalCount,
  TableWrapper,
  Table,
  Th,
  Td,
  Tr,
  StatusIcon,
  ErrorBanner,
  ResultBanner,
  ResultIcon,
  ResultTitle,
  ResultSubtext,
  ModalFooter,
  FooterRight,
  LoadingState,
} from '../shared/BulkUploadShared';
import { downloadErrorsCsv, downloadTemplateCsv } from '../shared/bulkUploadUtils';

type Step = 'upload' | 'preview' | 'result';

interface BulkLocationUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SAMPLE_HEADERS = ['name', 'street', 'city', 'state', 'zip', 'country', 'phone'];

const UploadActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const handleDownloadErrors = (rows: BulkLocationValidateRow[]) => {
  downloadErrorsCsv(
    rows.map((row) => ({
      row: row.row,
      name: row.name,
      street: row.street,
      city: row.city,
      state: row.state,
      zip: row.zip,
      country: row.country,
      phone: row.phone,
      valid: row.valid,
      reason: row.reason,
    })),
    SAMPLE_HEADERS,
    'location-upload-errors.csv',
  );
};

const handleDownloadSample = () => {
  downloadTemplateCsv(
    SAMPLE_HEADERS,
    [
      {
        name: 'Downtown Clinic',
        street: '123 Main St',
        city: 'Phoenix',
        state: 'AZ',
        zip: '85001',
        country: 'USA',
        phone: '+1-602-555-0100',
      },
    ],
    'location-upload-template.csv',
  );
};

export const BulkLocationUploadModal = ({
  isOpen,
  onClose,
}: BulkLocationUploadModalProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validateResult, setValidateResult] = useState<BulkLocationValidateResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [executeResult, setExecuteResult] = useState<{ created: number; skipped: number } | null>(
    null,
  );

  const validateMutation = useMutation({
    mutationFn: (file: File) => locationService.bulkValidateLocations(file),
    onSuccess: (result) => {
      setValidateResult(result);
      setStep('preview');
    },
    onError: () => {
      setUploadError('Failed to validate the CSV file. Please check the format and try again.');
    },
  });

  const executeMutation = useMutation({
    mutationFn: () => {
      const validRows = (validateResult?.rows ?? []).filter((row) => row.valid);
      return locationService.bulkExecuteLocations(validRows);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setExecuteResult({ created: result.created, skipped: result.skipped });
      setStep('result');
    },
    onError: () => {
      setUploadError('Failed to create locations. Please try again.');
    },
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setUploadError('Please upload a .csv file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 5 MB. Please upload a smaller file.');
      return;
    }
    setUploadError(null);
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setValidateResult(null);
    setUploadError(null);
    setExecuteResult(null);
    onClose();
  };

  const rows = validateResult?.rows ?? [];
  const validCount = rows.filter((row) => row.valid).length;
  const invalidCount = rows.filter((row) => !row.valid).length;

  const getTitle = () => {
    if (step === 'upload') return 'Bulk Upload Centers — Upload CSV';
    if (step === 'preview') return 'Bulk Upload Centers — Preview';
    return 'Bulk Upload Centers — Complete';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} size="lg">
      {step === 'upload' && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload CSV file"
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <UploadCloud />
            <DropLabel>Drop your CSV here or click to browse</DropLabel>
            <DropSubLabel>
              Accepted format: .csv — columns: name, street, city, state, zip, country, phone
            </DropSubLabel>
            {selectedFile && <FileNameChip>{selectedFile.name}</FileNameChip>}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />

          <UploadActions>
            <Button variant="ghost" leftIcon={<Download />} onClick={handleDownloadSample}>
              Download Sample CSV
            </Button>
          </UploadActions>

          {validateMutation.isPending && <LoadingState>Validating CSV...</LoadingState>}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => selectedFile && validateMutation.mutate(selectedFile)}
                disabled={!selectedFile || validateMutation.isPending}
                isLoading={validateMutation.isPending}
              >
                Upload & Validate
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {step === 'preview' && validateResult && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <SummaryBar>
            <ValidCount>
              <CheckCircle />
              {validCount} valid
            </ValidCount>
            <InvalidCount>
              <XCircle />
              {invalidCount} invalid
            </InvalidCount>
            <TotalCount>out of {rows.length} total</TotalCount>
          </SummaryBar>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Row</Th>
                  <Th>Name</Th>
                  <Th>Street</Th>
                  <Th>City</Th>
                  <Th>State</Th>
                  <Th>Zip</Th>
                  <Th>Country</Th>
                  <Th>Phone</Th>
                  <Th>Status</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.row} $invalid={!row.valid}>
                    <Td $invalid={!row.valid}>{row.row}</Td>
                    <Td $invalid={!row.valid}>{row.name ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.street ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.city ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.state ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.zip ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.country ?? '—'}</Td>
                    <Td $invalid={!row.valid}>{row.phone ?? '—'}</Td>
                    <Td>
                      <StatusIcon $valid={row.valid}>
                        {row.valid ? <CheckCircle /> : <XCircle />}
                      </StatusIcon>
                    </Td>
                    <Td $invalid={!row.valid}>{row.reason ?? '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <FooterRight>
              {invalidCount > 0 && (
                <Button
                  variant="ghost"
                  leftIcon={<Download />}
                  onClick={() => handleDownloadErrors(rows)}
                >
                  Download Errors
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => executeMutation.mutate()}
                disabled={validCount === 0 || executeMutation.isPending}
                isLoading={executeMutation.isPending}
              >
                Create {validCount} Center{validCount !== 1 ? 's' : ''}
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {step === 'result' && executeResult && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>Centers created!</ResultTitle>
            <ResultSubtext>
              {executeResult.created} center{executeResult.created !== 1 ? 's' : ''} created
              {executeResult.skipped > 0 && `, ${executeResult.skipped} skipped`}.
            </ResultSubtext>
          </ResultBanner>

          <ModalFooter>
            <div />
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default BulkLocationUploadModal;
